import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useThemeMode } from "../contexts/ThemeContext";
import { useStoredTheme } from "../utils/theme-context";
import { apFrontendUrl } from "../utils/ap-frontend-url";

// Handshake protocol shared with packages/web's PreviewFlowPage
// (src/app/routes/embed/preview-flow.tsx). Much narrower than
// ApBuilderIframe.jsx's real builder handshake: no AP session is minted, no
// project exists, nothing is created - the trigger data is already sitting in
// this component's props (from ExploreTemplates'/TemplateDetail's own cached
// template fetch), so once the child announces readiness we just hand it that
// data directly.
const PREVIEW_FLOW_READY = "AP_PREVIEW_FLOW_READY";
const PREVIEW_FLOW_DATA = "AP_PREVIEW_FLOW_DATA";
const PREVIEW_FLOW_THEME_CHANGE = "AP_PREVIEW_FLOW_THEME_CHANGE";

export function TemplateFlowPreview({ trigger }) {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const storedTheme = useStoredTheme();
  const accentColor = storedTheme?.button?.primary?.backgroundColor?.[mode];
  const iframeRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin !== apFrontendUrl ||
        event.source !== iframeRef.current?.contentWindow ||
        event.data?.type !== PREVIEW_FLOW_READY
      ) {
        return;
      }
      iframeRef.current?.contentWindow?.postMessage(
        { type: PREVIEW_FLOW_DATA, data: { trigger, theme: mode, accentColor } },
        apFrontendUrl,
      );
      setReady(true);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [trigger, mode, accentColor]);

  // Live theme/accent sync AFTER the handshake, mirroring ApBuilderIframe.jsx's
  // own effect - the initial values already travelled once above.
  useEffect(() => {
    if (!ready) {
      return;
    }
    iframeRef.current?.contentWindow?.postMessage(
      { type: PREVIEW_FLOW_THEME_CHANGE, data: { theme: mode, accentColor } },
      apFrontendUrl,
    );
  }, [mode, accentColor, ready]);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {!ready && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: theme.palette.background.default,
            zIndex: 1,
          }}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading preview...
          </Typography>
        </Box>
      )}
      <iframe
        ref={iframeRef}
        title="Flow Preview"
        src={`${apFrontendUrl}/templates/preview/flow`}
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    </Box>
  );
}
